/**
 * Sentry initialization for ImmigrantShield Offices (B2B).
 *
 * Init contract:
 *   - Only initializes when VITE_SENTRY_DSN is set. Local dev / preview builds
 *     without the env var are silently no-op so we never spam the production
 *     project with noise from `npm run dev`.
 *   - In dev (import.meta.env.DEV === true) we still no-op by default because
 *     hot-reload and React StrictMode double-renders generate enormous garbage.
 *     Set VITE_SENTRY_FORCE_DEV=1 to override when actually testing the
 *     integration locally.
 *   - sample rates are conservative on purpose. The project sits on the Sentry
 *     free tier (5k errors / 10M spans per month). At ~100 active offices we
 *     have ~50 errors/month/office of headroom — plenty.
 *
 * Tagging contract:
 *   - `office_id`, `office_plan`, `user_role` are populated by OfficeContext
 *     once the user's tenant is resolved (see setSentryOfficeContext below).
 *     This makes the Sentry dashboard filterable by tenant — critical for
 *     triaging "is this affecting one office or all of them?".
 *
 * What we deliberately DON'T do:
 *   - sendDefaultPii: false. Supabase JWTs and email addresses live in the
 *     Authorization header / form fields and we don't want them in Sentry.
 *   - replaysSessionSampleRate: 0. Session Replay is a separate quota line
 *     item (free tier: 50 replays/month) and not worth burning before we have
 *     SLOs that need it.
 */

import * as Sentry from '@sentry/react'

const DSN              = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENVIRONMENT      = (import.meta.env.VITE_SENTRY_ENV as string | undefined)
                       ?? (import.meta.env.DEV ? 'development' : 'production')
const RELEASE          = import.meta.env.VITE_SENTRY_RELEASE as string | undefined
const FORCE_DEV        = import.meta.env.VITE_SENTRY_FORCE_DEV === '1'

/** True once initSentry() has actually called Sentry.init(). */
let initialized = false

export function initSentry(): void {
  if (initialized) return
  if (!DSN) {
    if (!import.meta.env.DEV) {
      // Production build with no DSN configured — surface so we notice in
      // browser console during smoke tests, but don't throw.
      console.warn('[sentry] VITE_SENTRY_DSN not set; error reporting disabled')
    }
    return
  }
  if (import.meta.env.DEV && !FORCE_DEV) {
    // Silent no-op in dev unless explicitly forced.
    return
  }

  Sentry.init({
    dsn:         DSN,
    environment: ENVIRONMENT,
    release:     RELEASE,

    // PII protection — see header comment.
    sendDefaultPii: false,

    // 100 % of errors. We're nowhere near 5k/month.
    sampleRate: 1.0,

    // 10 % of transactions. Enough to spot perf regressions without burning
    // the spans quota. Bump when we have real traffic to analyze.
    tracesSampleRate: 0.1,

    integrations: [
      Sentry.browserTracingIntegration(),
    ],

    // beforeSend lets us filter known-noise BEFORE the event leaves the
    // browser. Keeps the quota clean and keeps the inbox actionable.
    beforeSend(event, hint) {
      const error = hint.originalException

      // 1. Ignore browser extensions injecting scripts. Common pattern:
      //    error.stack contains "chrome-extension://" or "moz-extension://".
      if (error instanceof Error && error.stack) {
        if (/chrome-extension:|moz-extension:|safari-extension:/.test(error.stack)) {
          return null
        }
      }

      // 2. Ignore ResizeObserver warnings — Chrome fires these spuriously and
      //    they're never actionable. The message is one of two specific strings.
      const msg = (event.message ?? '') + ' ' + (event.exception?.values?.[0]?.value ?? '')
      if (
        msg.includes('ResizeObserver loop completed with undelivered notifications') ||
        msg.includes('ResizeObserver loop limit exceeded')
      ) {
        return null
      }

      // 3. Ignore network failures from the user's side (offline, captive
      //    portal, ad blocker). These are not bugs in our code.
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError when attempting to fetch')) {
        return null
      }

      // 4. Ignore Supabase 401s — these happen by design when a session
      //    expires and the user needs to re-login. AuthContext handles them.
      const status = (error as { status?: number } | undefined)?.status
      if (status === 401) return null

      return event
    },

    // Extra paranoid filter — applies to messages reported via captureMessage.
    ignoreErrors: [
      // Network / browser noise
      'Network request failed',
      'Load failed',
      'NetworkError',
      'AbortError',
      'cancelled',
      // Common React DevTools / extension chatter
      "Can't find variable: __REACT_DEVTOOLS_GLOBAL_HOOK__",
    ],
  })

  initialized = true
}

/**
 * Attach (or clear) the current tenant context. Called by OfficeContext
 * whenever the active office changes — including logout (pass null).
 *
 * The user_id goes into Sentry's user object so it's queryable but is the
 * Supabase auth UUID, not an email. The office_id / plan / role go into
 * tags so they show up as filterable facets in the Sentry UI.
 */
export function setSentryOfficeContext(ctx: {
  userId:     string | null
  officeId:   string | null
  officePlan: string | null
  userRole:   string | null
}): void {
  if (!initialized) return

  if (ctx.userId) {
    // No email, no IP — just the opaque ID. Enough to correlate to support
    // tickets without exfiltrating PII.
    Sentry.setUser({ id: ctx.userId })
  } else {
    Sentry.setUser(null)
  }

  Sentry.setTag('office_id',   ctx.officeId   ?? 'none')
  Sentry.setTag('office_plan', ctx.officePlan ?? 'none')
  Sentry.setTag('user_role',   ctx.userRole   ?? 'none')
}

/**
 * Manual capture wrapper. Use when you want to report an exception that you
 * already caught and handled gracefully (so the user didn't see it) but which
 * is still a bug worth knowing about.
 */
export function captureHandledError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) {
    if (import.meta.env.DEV) console.error('[sentry not initialized]', err, context)
    return
  }
  Sentry.captureException(err, { extra: context })
}

/** True if Sentry is live in this session. Safe to call before initSentry(). */
export function sentryIsLive(): boolean {
  return initialized
}
